import { useMemo } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faUser } from "@fortawesome/free-solid-svg-icons"
import { useAppSelector } from "@/core/root-reducer"
import riskStore from "@/features/risks/entities/risk/model/risk.store"
import UnitRiskHeatmap from "@/features/risks/entities/unit/ui/UnitRiskHeatmap"
import RiskTypeChip from "@/features/risks/entities/risk/ui/RiskTypeChip"
import Tooltip from "@/components/tooltip"
import { t } from "i18next"
import { Unit } from "../model/unit.types"

interface Props {
  unit: Unit
  onClick?: () => void
  hovered: boolean
}

export default function UnitNodeCard({ unit, onClick, hovered = false }: Props) {
  const risks = useAppSelector(riskStore.selectors.list)

  const { rpsItems, phyItems, rpsCount, phyCount } = useMemo(() => {
    const validated = risks.filter((r) => r.UnitId === unit.id && r.dateValidated)

    // Séparation et tri par type (RPS vs PHY) et par gravité décroissante
    const rps = validated
      .filter((r) => r.rps)
      .sort((a, b) => (b.valueEffective ?? 0) - (a.valueEffective ?? 0))
      .map((risk) => ({
        color: risk.valueEffectiveColor,
        title: `${risk.valueEffectiveLabel} • ${risk.name ?? ""}`
      }))

    const phy = validated
      .filter((r) => !r.rps)
      .sort((a, b) => (b.valueEffective ?? 0) - (a.valueEffective ?? 0))
      .map((risk) => ({
        color: risk.valueEffectiveColor,
        title: `${risk.valueEffectiveLabel} • ${risk.name ?? ""}`
      }))

    return {
      rpsItems: rps,
      phyItems: phy,
      rpsCount: rps.length,
      phyCount: phy.length
    }
  }, [risks, unit.id])

  // 13 dots par row
  const rpsColsCount = 10
  const phyColsCount = 10

  return (
    <div
      onClick={onClick}
      className={`h-full w-[90%] relative flex flex-col justify-between rounded-lg border bg-white cursor-pointer
        transition-all duration-200 shadow-md ${
          hovered ? "shadow-lg -translate-y-0.5 border-gray-400" : "border-gray-300"
        } py-[20px] px-[30px] 
      `}>
      {/* --- Header --- */}
      <div className="flex w-full items-start justify-between">
        <h2 className="font-semibold text-lg leading-tight w-[75%] mt-0 overflow-hidden line-clamp-2">{unit.name}</h2>
        <div className="flex text-sm ml-auto grey-t items-center gap-10">
          <FontAwesomeIcon icon={faUser} />
          {unit.size != null ? unit.size : "NC"}
        </div>
      </div>

      {/* --- Content --- */}
      <div className="flex flex-col gap-3 mt-auto w-full">
        {/* --- PHY Row --- */}
        <div className="flex items-center gap-2 justify-between">
          <div className="min-w-0 flex-1 flex items-center">
            {phyCount > 0 ? (
              <UnitRiskHeatmap items={phyItems} size={8} gap={2} cols={phyColsCount} />
            ) : rpsCount === 0 && phyCount === 0 ? (
              <span className="text-sm grey-t">{t("Aucun risque")}</span>
            ) : null}
          </div>
          <div className="flex-shrink-0 w-16">
            {phyCount > 0 ? (
              <RiskTypeChip
                isRps={false}
                count={phyCount}
                showCount
                color={phyItems[0]?.color || undefined}
                isSmall
                showLabel={false}
                tooltipPosition="left"
              />
            ) : (
              <Tooltip text="Aucun risque PHY" position="left">
                <RiskTypeChip
                  isRps={false}
                  count={0}
                  showCount
                  color="#ccc"
                  isSmall
                  showLabel={false}
                  tooltipPosition="left"
                />
              </Tooltip>
            )}
          </div>
        </div>

        {/* --- RPS Row --- */}
        <div className="flex items-center gap-2 justify-between">
          <div className="min-w-0 flex-1">
            {rpsCount > 0 && <UnitRiskHeatmap items={rpsItems} size={8} gap={2} cols={rpsColsCount} />}
          </div>
          <div className="flex-shrink-0 w-16">
            {rpsCount > 0 ? (
              <RiskTypeChip
                isRps={true}
                count={rpsCount}
                showCount
                color={rpsItems[0]?.color || undefined}
                isSmall
                showLabel={false}
                tooltipPosition="left"
              />
            ) : (
              <Tooltip text="Aucun risque RPS" position="left">
                <RiskTypeChip
                  isRps={true}
                  count={0}
                  showCount
                  color="#ccc"
                  isSmall
                  showLabel={false}
                  tooltipPosition="left"
                />
              </Tooltip>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
